import React from 'react';
import { Flex, ButtonV2, FormControl, InputV2, SpacingStack, SelectV1, OptionType, Divider, TextV2, DialogV2, DrawerV2 } from '@withjoy/joykit';
import { useFormController } from './Form.controller';
import { Wrapper } from './components';
import 'react-intl-tel-input/dist/main.css';
import { StyledFormControl } from './ShippingAddress.style';
import IntlTelInput from 'react-intl-tel-input';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { DIALOG_OVERRIDES, DRAWER_OVERRIDES } from './ShippingAddress.style';
import { ShippingDialogView } from './ShippingAddress';

interface FormProps extends ReturnType<typeof useFormController> {
  title?: string;
  subtitle?: string;
}

export const FormDialogWrapper: React.FC<{ isOpen: boolean; onClose: () => void; disableUserClose?: boolean; view: ShippingDialogView } & FormProps> = ({
  isOpen,
  onClose,
  disableUserClose,
  view,
  ...restProps
}) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { t2 } = useTranslation('sharedComponents');
  const { saveAddressBtn, clearBtn, shippingAdressFormTitle } = t2('shippingAddressDialog');
  return !isMobile ? (
    <DialogV2
      size="xl"
      isOpen={isOpen}
      overrides={DIALOG_OVERRIDES}
      onClose={onClose}
      disableCloseOnEscapeKeyClick={disableUserClose}
      disableCloseOnOutsideClick={disableUserClose}
      stickyBehavior="header-footer"
    >
      <DialogV2.Header>
        <TextV2 typographyVariant="hed2">{shippingAdressFormTitle}</TextV2>
        {!disableUserClose && <DialogV2.CloseButton />}
      </DialogV2.Header>
      <DialogV2.Body>
        <Form {...restProps} />
      </DialogV2.Body>
      <DialogV2.Footer>
        <SpacingStack stack="horizontal" justifyContent="space-between" width="100%">
          <ButtonV2 marginTop="32px" onClick={() => restProps.formik.resetForm()} intent="neutral" variant="ghost">
            {clearBtn}
          </ButtonV2>
          <ButtonV2 marginTop="32px" onClick={() => restProps.handleOValidateAndSubmitAddress()} disabled={!restProps.isFormValid} intent="neutral">
            {saveAddressBtn}
          </ButtonV2>
        </SpacingStack>
      </DialogV2.Footer>
    </DialogV2>
  ) : (
    <DrawerV2
      onClose={onClose}
      disableAutoFocus
      size="auto"
      overrides={DRAWER_OVERRIDES}
      isOpen={isOpen}
      anchor="bottom"
      useBackdrop
      disableCloseOnEscapeKeyClick={disableUserClose}
      disableCloseOnOutsideClick={disableUserClose}
    >
      <DrawerV2.Header>
        <TextV2 typographyVariant="hed2">{shippingAdressFormTitle}</TextV2>
        {!disableUserClose && <DrawerV2.CloseButton />}
      </DrawerV2.Header>
      <DrawerV2.Body>
        <Form {...restProps} />
      </DrawerV2.Body>
    </DrawerV2>
  );
};

export const Form: React.FC<FormProps> = ({
  getFieldProps,
  isFormValid,
  errors: { firstNameError, address1Error, postalCodeError, cityError, regionError, countryError },
  countryList,
  handleOnCountryDropdownChange,
  formik,
  handleOnPhoneNumberChange,
  handleOnPhoneNumberFocus,
  isPhoneNumberInvalid,
  handleOValidateAndSubmitAddress
}) => {
  const { t2 } = useTranslation('sharedComponents');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { saveAddressBtn, clearBtn, name, mobilePhone, address1, address2, city, zipCode, state, country, nameSubtext, address1Subtext, requiredText, phoneDisclaimer } = t2(
    'shippingAddressDialog'
  );
  return (
    <>
      <Divider />
      <Wrapper bigger={true}>
        <Flex flexDirection="column" justifyContent="center" paddingX={['24px', null, 0]} width="100%">
          <form style={{ width: '100%' }}>
            <SpacingStack spacing={6}>
              <FormControl label={`${name}${'\u2009*'}`} isInvalid={!!firstNameError} error={firstNameError} isRequired caption={nameSubtext}>
                <InputV2 {...getFieldProps('name')} autoComplete="name" />
              </FormControl>
              <StyledFormControl label={mobilePhone} isInvalid={isPhoneNumberInvalid} error={isPhoneNumberInvalid ? 'Invalid Phone Number' : ''} caption={phoneDisclaimer}>
                <IntlTelInput
                  {...getFieldProps('phoneNumber')}
                  autoComplete="tel"
                  preferredCountries={['us']}
                  onPhoneNumberChange={handleOnPhoneNumberChange}
                  onPhoneNumberFocus={handleOnPhoneNumberFocus}
                />
              </StyledFormControl>
              <SpacingStack spacing={3} stack="horizontal">
                <FormControl label={`${country}${'\u2009*'}`} isInvalid={!!countryError} error={countryError} width="100%">
                  <SpacingStack spacing={3}>
                    <SelectV1
                      options={countryList}
                      value={{ label: formik.values.country, value: formik.values.countryCode } as OptionType}
                      searchable={false}
                      onChange={handleOnCountryDropdownChange}
                    />
                  </SpacingStack>
                </FormControl>
                <FormControl label={`${zipCode}${'\u2009*'}`} isInvalid={!!postalCodeError} error={postalCodeError} isRequired width="100%">
                  <InputV2 {...getFieldProps('postalCode')} autoComplete="postal-code" />
                </FormControl>
              </SpacingStack>
              <FormControl label={`${address1}${'\u2009*'}`} isInvalid={!!address1Error} error={address1Error} isRequired caption={address1Subtext}>
                <InputV2 {...getFieldProps('address1')} autoComplete="street-address" />
              </FormControl>
              <FormControl label={address2}>
                <InputV2 {...getFieldProps('address2')} autoComplete="address-line2" />
              </FormControl>
              <SpacingStack spacing={3} stack="horizontal">
                <FormControl label={`${city}${'\u2009*'}`} isInvalid={!!cityError} error={cityError} isRequired width="100%">
                  <InputV2 {...getFieldProps('city')} autoComplete="address-level2" />
                </FormControl>
                <FormControl label={`${state}${'\u2009*'}`} isInvalid={!!regionError} error={regionError} isRequired width="100%">
                  <InputV2 {...getFieldProps('state')} autoComplete="address-level1" />
                </FormControl>
              </SpacingStack>
            </SpacingStack>
            <TextV2 typographyVariant="label3" marginY={6}>
              {requiredText}
            </TextV2>
            <Divider />
            {isMobile && (
              <SpacingStack stack="horizontal" justifyContent="space-between">
                <ButtonV2 marginTop="32px" onClick={() => formik.resetForm()} intent="neutral" variant="ghost">
                  {clearBtn}
                </ButtonV2>
                <ButtonV2 marginTop="32px" onClick={() => handleOValidateAndSubmitAddress()} disabled={!isFormValid} intent="neutral">
                  {saveAddressBtn}
                </ButtonV2>
              </SpacingStack>
            )}
          </form>
        </Flex>
      </Wrapper>
    </>
  );
};
